import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Toolbar,
  Card,
  CardContent,
  IconButton,
} from "@material-ui/core";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import NodeConfigModal from "./Modals/NodeConfigModal";

const useStyles = makeStyles((theme) => ({
  editorContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 120px)",
    width: "100%",
  },
  toolbar: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  content: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  sidebar: {
    width: 250,
    padding: theme.spacing(2),
    borderRight: `1px solid ${theme.palette.divider}`,
    overflowY: "auto",
    backgroundColor: theme.palette.background.paper,
  },
  canvas: {
    flex: 1,
    padding: theme.spacing(2),
    overflow: "auto",
    backgroundColor: theme.palette.background.default,
    position: "relative",
    minHeight: 400,
    backgroundImage: "radial-gradient(circle, #e0e0e0 1px, transparent 1px)",
    backgroundSize: "20px 20px",
  },
  nodeCard: {
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  flowNode: {
    position: "absolute",
    minWidth: 200,
    cursor: "move",
    zIndex: 1,
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  flowNodeDragging: {
    opacity: 0.7,
    zIndex: 1000,
  },
  flowNodeConnecting: {
    border: "2px solid #1976d2",
  },
}));

const FlowEditorSimple = ({ flowId, initialFlow, onSave, onCancel }) => {
  const classes = useStyles();
  const canvasRef = useRef(null);
  const [flowName, setFlowName] = useState(initialFlow?.name || "");
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draggingNode, setDraggingNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [connectingFrom, setConnectingFrom] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  // ✅ Cargar flujo inicial
  useEffect(() => {
    if (initialFlow?.flow) {
      try {
        const flowData = typeof initialFlow.flow === "string" 
          ? JSON.parse(initialFlow.flow) 
          : initialFlow.flow;
        
        if (flowData.nodes) {
          setNodes(flowData.nodes || []);
        }
        if (flowData.edges) {
          setEdges(flowData.edges || []);
        }
      } catch (error) {
        console.error("Error parsing flow data:", error);
        setNodes([]);
        setEdges([]);
      }
    }
  }, [initialFlow]);

  // ✅ Nodos disponibles para agregar
  const availableNodes = [
    { type: "start", label: "Inicio", icon: "🚀" },
    { type: "message", label: "Mensaje", icon: "💬" },
    { type: "condition", label: "Condición", icon: "🔀" },
  ];

  const handleAddNode = (nodeType) => {
    const newNode = {
      id: `node-${Date.now()}`,
      type: nodeType,
      label: availableNodes.find(n => n.type === nodeType)?.label || nodeType,
      position: { x: 100, y: nodes.length * 150 + 100 },
      data: {},
    };
    setNodes([...nodes, newNode]);
  };

  const handleDeleteNode = (nodeId) => {
    setNodes(nodes.filter(n => n.id !== nodeId));
    // Eliminar conexiones relacionadas
    setEdges(edges.filter(e => e.source !== nodeId && e.target !== nodeId));
  };

  // ✅ Calcular posición de conexión en un nodo
  const getNodeConnectionPoint = (node, isSource = true) => {
    const nodeElement = document.getElementById(`node-${node.id}`);
    if (!nodeElement) return { x: node.position.x + 100, y: node.position.y + 30 };
    
    const rect = nodeElement.getBoundingClientRect();
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) return { x: node.position.x + 100, y: node.position.y + 30 };
    
    return {
      x: rect.left - canvasRect.left + (isSource ? rect.width : 0),
      y: rect.top - canvasRect.top + rect.height / 2,
    };
  };

  // ✅ Manejar inicio de conexión
  const handleStartConnection = (nodeId) => {
    setConnectingFrom(nodeId);
  };

  // ✅ Manejar finalizar conexión
  const handleEndConnection = (targetNodeId) => {
    if (connectingFrom && connectingFrom !== targetNodeId) {
      // Verificar que no existe ya la conexión
      const exists = edges.some(
        e => e.source === connectingFrom && e.target === targetNodeId
      );
      
      if (!exists) {
        const newEdge = {
          id: `edge-${Date.now()}`,
          source: connectingFrom,
          target: targetNodeId,
        };
        setEdges([...edges, newEdge]);
      }
    }
    setConnectingFrom(null);
  };

  // ✅ Eliminar conexión
  const handleDeleteEdge = (edgeId) => {
    setEdges(edges.filter(e => e.id !== edgeId));
  };

  // ✅ Abrir modal de configuración
  const handleOpenConfig = (node) => {
    setSelectedNode(node);
    setConfigModalOpen(true);
  };

  // ✅ Guardar configuración de nodo
  const handleSaveNodeConfig = (updatedNode) => {
    setNodes(nodes.map(n => n.id === updatedNode.id ? updatedNode : n));
    setConfigModalOpen(false);
    setSelectedNode(null);
  };

  // ✅ Drag handlers (eventos nativos)
  const handleMouseDown = useCallback((e, node) => {
    if (e.button !== 0) return; // Solo botón izquierdo
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const nodeElement = e.currentTarget;
    const nodeRect = nodeElement.getBoundingClientRect();
    
    setDraggingNode(node.id);
    setDragOffset({
      x: e.clientX - nodeRect.left,
      y: e.clientY - nodeRect.top,
    });
    
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!draggingNode) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragOffset.x;
    const newY = e.clientY - rect.top - dragOffset.y;

    setNodes(prevNodes =>
      prevNodes.map(node =>
        node.id === draggingNode
          ? { ...node, position: { x: Math.max(0, newX), y: Math.max(0, newY) } }
          : node
      )
    );
  }, [draggingNode, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setDraggingNode(null);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  // ✅ Event listeners para drag
  useEffect(() => {
    if (draggingNode) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingNode]);

  const handleSave = async () => {
    if (!flowName.trim()) {
      toast.error(i18n.t("flowbuilders.validation.nameRequired"));
      return;
    }

    try {
      setSaving(true);
      const flowData = {
        nodes,
        edges,
      };

      await onSave({
        id: flowId,
        flow: flowData,
        name: flowName,
      }, false);

      toast.success(i18n.t("flowbuilders.toasts.saved"));
    } catch (error) {
      toast.error(i18n.t("flowbuilders.toasts.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className={classes.editorContainer}>
      {/* ✅ Toolbar */}
      <Toolbar className={classes.toolbar}>
        <TextField
          value={flowName}
          onChange={(e) => setFlowName(e.target.value)}
          placeholder={i18n.t("flowbuilders.editor.newFlow")}
          variant="outlined"
          size="small"
          style={{ minWidth: 200 }}
        />
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            style={{ marginRight: 8 }}
          >
            {i18n.t("flowbuilders.editor.save")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
          >
            {i18n.t("flowbuilders.editor.cancel")}
          </Button>
        </Box>
      </Toolbar>

      {/* ✅ Contenido */}
      <Box className={classes.content}>
        {/* ✅ Sidebar con nodos disponibles */}
        <Paper className={classes.sidebar} elevation={0}>
          <Typography variant="h6" style={{ marginBottom: 16, fontWeight: "bold" }}>
            {i18n.t("flowbuilders.editor.nodesPanel")}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ marginBottom: 16, display: "block" }}>
            {i18n.t("flowbuilders.editor.nodesHint")}
          </Typography>
          {availableNodes.map((node) => (
            <Card
              key={node.type}
              className={classes.nodeCard}
              onClick={() => handleAddNode(node.type)}
            >
              <CardContent style={{ padding: 12 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <span style={{ fontSize: 20 }}>{node.icon}</span>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {node.label}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
          <Box style={{ marginTop: 24, padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
            <Typography variant="caption" style={{ display: "block", marginBottom: 4, fontWeight: "bold" }}>
              {i18n.t("flowbuilders.editor.shortcuts")}
            </Typography>
            <Typography variant="caption" style={{ display: "block", fontSize: 11 }}>
              • {i18n.t("flowbuilders.editor.shortcutDrag")}
            </Typography>
            <Typography variant="caption" style={{ display: "block", fontSize: 11 }}>
              • {i18n.t("flowbuilders.editor.shortcutConnect")}
            </Typography>
            <Typography variant="caption" style={{ display: "block", fontSize: 11 }}>
              • {i18n.t("flowbuilders.editor.shortcutConfig")}
            </Typography>
          </Box>
        </Paper>

        {/* ✅ Canvas */}
        <Box 
          ref={canvasRef}
          className={classes.canvas}
        >
          {/* ✅ SVG para conexiones (debajo de los nodos) */}
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            {edges.map((edge) => {
              const sourceNode = nodes.find(n => n.id === edge.source);
              const targetNode = nodes.find(n => n.id === edge.target);
              
              if (!sourceNode || !targetNode) return null;
              
              const sourcePoint = getNodeConnectionPoint(sourceNode, true);
              const targetPoint = getNodeConnectionPoint(targetNode, false);
              
              // Calcular punto de control para curva
              const dx = targetPoint.x - sourcePoint.x;
              const controlX = sourcePoint.x + dx * 0.5;
              
              return (
                <g key={edge.id || `edge-${edge.source}-${edge.target}`}>
                  <path
                    d={`M ${sourcePoint.x} ${sourcePoint.y} C ${controlX} ${sourcePoint.y}, ${controlX} ${targetPoint.y}, ${targetPoint.x} ${targetPoint.y}`}
                    stroke="#1976d2"
                    strokeWidth="2"
                    fill="none"
                    markerEnd="url(#arrowhead)"
                    style={{ pointerEvents: "stroke", cursor: "pointer" }}
                    onClick={() => {
                      if (edge.id) {
                        handleDeleteEdge(edge.id);
                      }
                    }}
                  />
                </g>
              );
            })}
            {/* ✅ Flecha para las conexiones */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 10 3, 0 6" fill="#1976d2" />
              </marker>
            </defs>
          </svg>

          {nodes.length === 0 ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
            >
              <Typography variant="body2" color="textSecondary">
                {i18n.t("flowbuilders.editor.noNodes")}
              </Typography>
            </Box>
          ) : (
            nodes.map((node) => {
              const isConnecting = connectingFrom === node.id;
              return (
                <Card
                  key={node.id}
                  id={`node-${node.id}`}
                  className={`${classes.flowNode} ${draggingNode === node.id ? classes.flowNodeDragging : ""} ${isConnecting ? classes.flowNodeConnecting : ""}`}
                  style={{
                    left: `${node.position.x}px`,
                    top: `${node.position.y}px`,
                    zIndex: draggingNode === node.id ? 1000 : 1,
                    border: isConnecting ? "2px solid #1976d2" : "none",
                  }}
                  onMouseDown={(e) => {
                    if (e.shiftKey) {
                      // Shift + Click para iniciar conexión
                      e.stopPropagation();
                      handleStartConnection(node.id);
                    } else if (e.detail === 2) {
                      // Doble click para configurar
                      e.stopPropagation();
                      handleOpenConfig(node);
                    } else {
                      handleMouseDown(e, node);
                    }
                  }}
                  onMouseUp={(e) => {
                    if (connectingFrom && connectingFrom !== node.id) {
                      e.stopPropagation();
                      handleEndConnection(node.id);
                    }
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box 
                        style={{ flex: 1, cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenConfig(node);
                        }}
                      >
                        <Typography variant="body1" style={{ fontWeight: "bold" }}>
                          {node.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {node.type}
                        </Typography>
                        {node.data?.text && (
                          <Typography variant="caption" style={{ display: "block", marginTop: 4, fontStyle: "italic" }}>
                            {node.data.text.substring(0, 30)}...
                          </Typography>
                        )}
                        {isConnecting && (
                          <Typography variant="caption" style={{ color: "#1976d2", display: "block", marginTop: 4 }}>
                            {i18n.t("flowbuilders.editor.connecting")}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNode(node.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>
      </Box>

      {/* ✅ Modal de Configuración de Nodo */}
      <NodeConfigModal
        open={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setSelectedNode(null);
        }}
        node={selectedNode}
        onSave={handleSaveNodeConfig}
      />
    </Box>
  );
};

export default FlowEditorSimple;
